import { useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, FlaskConical, GraduationCap, MessageCircle, MessageSquare, NotebookPen, Pencil, RefreshCcw, Star, Trash, Users, Wrench } from 'lucide-react';
import Button from '../../../components/Button';
import { ButtonWithConfirmation } from '../../../components/ButtonWithConfirmation';
import { useAuth } from '../../../hooks/useAuth';
import { questsService } from '../services/quests-service';
import type { QuestTemplateChatFirestore, QuestTemplateFirestore, QuestType } from '../services/questTypes';
import { ChatQuestAiTest } from './ChatQuestAiTest';
import { JourneyDaySelector } from './JourneyDaySelector';

interface DisplayQuestTemplateProps {
    questTemplate: QuestTemplateFirestore;
    journeyDayId: string;
    availableJourneyDays: string[];
    onEdit?: () => void;
    onDelete?: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    onMoveToDay?: (targetJourneyDayId: string) => void;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
}

export const DisplayQuestTemplate = ({ questTemplate, journeyDayId, availableJourneyDays, onEdit, onDelete, onMoveUp, onMoveDown, onMoveToDay, canMoveUp = true, canMoveDown = true }: DisplayQuestTemplateProps) => {
    const { user } = useAuth();
    const [isTestModalOpen, setIsTestModalOpen] = useState(false);

    const getQuestTypeIcon = (type: QuestType): React.ReactNode => {
        const iconClass = "w-5 h-5";
        const icons: Record<QuestType, React.ReactNode> = {
            'chat': <MessageCircle className={iconClass} />,
            'check-in': <NotebookPen className={iconClass} />,
            'story': <BookOpen className={iconClass} />,
            'lesson': <GraduationCap className={iconClass} />,
            'community-question': <Users className={iconClass} />,
            'tool': <Wrench className={iconClass} />,
            'feedback': <MessageSquare className={iconClass} />,
            'rate': <Star className={iconClass} />
        };
        return icons[type];
    };

    const getQuestTypeLabel = (type: string): string => {
        const labels: Record<string, string> = {
            'chat': 'Chat',
            'story': 'Story',
            'lesson': 'Lesson',
            'community-question': 'Community Question',
            'tool': 'Tool',
            'feedback': 'Feedback',
            'rate': 'Rate'
        };
        return labels[type] || type;
    };

    const getProgressTypeLabel = (progressType: string): string => {
        return progressType === 'fixed' ? 'Fixed' : 'Undetermined';
    };

    const getQuestCardColor = (isPublished: boolean): string => {
        return isPublished ? '' : 'border-dashed border-red-500';
    };

    const handleResetAllCollectedValues = async () => {
        const chatQuestTemplate = questTemplate as QuestTemplateChatFirestore;
        await questsService.resetFieldCollectedValues(user!.uid, chatQuestTemplate.fieldsToCollect.map(field => field.key));
    };

    const handleOpenTestModal = () => {
        setIsTestModalOpen(true);
    };

    return (
        <div className={`border-2 border-gray-200 rounded-lg p-3 space-y-2 ${getQuestCardColor(questTemplate.isPublished)}`}>
            {/* Header with Edit Button */}
            <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <div className="flex flex-wrap items-center gap-2 text-base font-semibold text-gray-900">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        #{questTemplate.order}
                    </span>
                    {getQuestTypeIcon(questTemplate.type)}
                    <span>{getQuestTypeLabel(questTemplate.type)}</span>
                    {questTemplate.isPrimary && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Primary
                        </span>
                    )}
                    {questTemplate.isOptional && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Optional
                        </span>
                    )}
                    {!questTemplate.isOptional && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Required
                        </span>
                    )}
                    {questTemplate.isPrerequisite && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Prerequisite
                        </span>
                    )}
                    {questTemplate.isFree && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Free
                        </span>
                    )}
                </div>
                <span className='text-xs font-medium text-gray-500'> {questTemplate.isPublished ? 'Published' : 'Unpublished'}</span>
                <div className="flex items-center gap-1">
                    {onMoveUp && (
                        <Button
                            variant="ghost"
                            size="sm"
                            icon={<ChevronUp className="w-4 h-4" />}
                            onClick={onMoveUp}
                            disabled={!canMoveUp}
                            title="Move up"
                        />
                    )}
                    {onMoveDown && (
                        <Button
                            variant="ghost"
                            size="sm"
                            icon={<ChevronDown className="w-4 h-4" />}
                            onClick={onMoveDown}
                            disabled={!canMoveDown}
                            title="Move down"
                        />
                    )}
                    {onMoveToDay && (
                        <JourneyDaySelector
                            availableJourneyDays={availableJourneyDays}
                            currentJourneyDayId={journeyDayId}
                            onSelect={onMoveToDay}
                        />
                    )}
                    {onDelete && (
                        <Button
                            onClick={onDelete}
                            variant="danger"
                            size="sm"
                            icon={<Trash size={16} />}
                        />
                    )}
                    {onEdit && (
                        <Button
                            onClick={onEdit}
                            size="sm"
                            icon={<Pencil size={16} />}
                        />
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {/* Progress Type */}
                <div className="p-2">
                    <label className="block text-xs font-medium text-gray-500">
                        Progress
                    </label>
                    <div className="text-sm font-medium text-gray-900">
                        {getProgressTypeLabel(questTemplate.progressType)}
                    </div>
                </div>

                {/* Max Progress (if applicable) */}
                {questTemplate.maxProgress !== undefined && (
                    <div className="p-2">
                        <label className="block text-xs font-medium text-gray-500">
                            Max
                        </label>
                        <div className="text-sm font-medium text-gray-900">
                            {questTemplate.maxProgress}
                        </div>
                    </div>
                )}
                {/* Pill Text */}
                {questTemplate.pillText !== undefined && (
                    <div className="p-2">
                        <label className="block text-xs font-medium text-gray-500">
                            Pill Text
                        </label>
                        <div className="text-sm font-medium text-gray-900">
                            {questTemplate.pillText}
                        </div>
                    </div>
                )}
            </div>

            {/* Text Templating Prompt */}
            <div className="p-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                    Prompt
                </label>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {questTemplate.textTemplatingPrompt || <span className="text-gray-400 italic">No prompt provided</span>}
                </p>
            </div>
            {questTemplate.type === 'chat' && (
                <>
                    <div className="flex flex-wrap gap-2 p-2">
                        <ButtonWithConfirmation
                            onClick={handleResetAllCollectedValues}
                            firstText="Reset all collected values"
                            secondText="Really reset all collected values?"
                            firstIcon={<RefreshCcw className="w-4 h-4" />}
                            secondIcon={<RefreshCcw className="w-4 h-4" />}
                            firstVariant="outline"
                            secondVariant="danger"
                            size="sm"
                        />
                        <Button
                            onClick={handleOpenTestModal}
                            variant="outline"
                            size="sm"
                            icon={<FlaskConical className="w-4 h-4" />}
                            title="Test chat quest"
                        >
                            Test with AI
                        </Button>
                    </div>
                    <ChatQuestAiTest
                        isOpen={isTestModalOpen}
                        onClose={() => setIsTestModalOpen(false)}
                        journeyDayId={journeyDayId}
                        questTemplateId={questTemplate.id!}
                        questType="chat"
                    />
                </>
            )}
            {questTemplate.type === 'check-in' && (
                <>
                    <div className="flex flex-wrap gap-2 p-2">
                        <Button
                            onClick={handleOpenTestModal}
                            variant="outline"
                            size="sm"
                            icon={<FlaskConical className="w-4 h-4" />}
                            title="Test check-in quest"
                        >
                            Test with AI
                        </Button>
                    </div>
                    <ChatQuestAiTest
                        isOpen={isTestModalOpen}
                        onClose={() => setIsTestModalOpen(false)}
                        journeyDayId={journeyDayId}
                        questTemplateId={questTemplate.id!}
                        questType="check-in"
                    />
                </>
            )}
        </div>
    );
};